import {Clipboard} from '@angular/cdk/clipboard';
import {AsyncPipe} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar, MatSnackBarRef} from '@angular/material/snack-bar';
import {AssetService, AssetType} from '@app/asset';
import {AssetLinkService} from '@app/asset-link/asset-link.service';
import {DownloadService} from '@app/download/download.service';
import {GroupService} from '@app/group/group.service';
import {SelectionSnackbarComponent} from '@app/selection/selection-snackbar/selection-snackbar.component';
import {DeleteAssetDialogComponent} from '@app/shared/delete-asset-dialog/delete-asset-dialog.component';
import {StorageService} from '@app/shared/storage.service';
import {StateService} from '@app/state/state.service';
import {Stream} from '@app/stream/models';
import {StreamService} from '@app/stream/stream.service';

import {Observable} from 'rxjs';
import {filter, map, take, tap} from 'rxjs/operators';

import {SelectionService} from '../selection.service';
import {SelectionComponent} from '../selection/selection.component';

@Component({
    selector: 'frog-selection-toolbar',
    templateUrl: './selection-toolbar.component.html',
    styleUrls: ['./selection-toolbar.component.scss'],
    imports: [SelectionComponent, AsyncPipe],
})
export class SelectionToolbarComponent implements OnInit {
    public selection$: Observable<string[]>;
    public stateStream$: Observable<string>;
    public streams$: Observable<Stream[]>;
    public selectionMode$: Observable<boolean>;
    private snackBarRef: MatSnackBarRef<SelectionSnackbarComponent>;

    constructor(
        private selectionService: SelectionService,
        private groupService: GroupService,
        private assetService: AssetService,
        private stateService: StateService,
        private streamService: StreamService,
        private storageService: StorageService,
        private assetLinkService: AssetLinkService,
        private downloadService: DownloadService,
        private matDialog: MatDialog,
        private matSnackBar: MatSnackBar,
        private clipboard: Clipboard,
    ) {
        this.snackBarRef = null;
    }

    ngOnInit(): void {
        this.selection$ = this.selectionService.selection;
        this.stateStream$ = this.stateService.state.pipe(
            filter((state) => Boolean(state.streamId)),
            map((state) => state.streamId),
        );
        this.streams$ = this.streamService.streams.pipe(
            map((streams) => streams.filter((stream) => stream.parent === null)),
            map((streams) => streams.sort()),
        );

        this.selectionMode$ = this.selectionService.selectionMode.pipe(
            tap((value) => {
                if (this.snackBarRef !== null) {
                    this.snackBarRef.dismiss();
                }
                if (value) {
                    // Enable selection mode
                    this.snackBarRef = this.matSnackBar.openFromComponent(
                        SelectionSnackbarComponent,
                        {
                            verticalPosition: 'top',
                            horizontalPosition: 'end',
                        },
                    );
                }
            }),
        );
    }

    clearSelection() {
        this.selectionService.clearSelection();
    }

    groupSelection(assetIds: string[], streamId: string) {
        this.assetService.resolve(assetIds).subscribe((assets) => {
            // First, try to find a group in the selection
            const group = assets.find((asset) => asset.asset_type === AssetType.Group);
            if (group) {
                const assetIds = assets
                    .filter((asset) => asset.asset_type !== AssetType.Group)
                    .map((asset) => asset.id);
                this.groupService.append(group.id, assetIds).subscribe();
            } else {
                // No group found, so we create one
                const assetIds = assets.map((asset) => asset.id);
                this.groupService
                    .create(assetIds, streamId)
                    .subscribe((groupAsset) => this.assetService.filter(true));
            }
            this.selectionService.clearSelection();
        });
    }

    cut(assetIds: string[], streamId: string) {
        this.storageService.set('clipboard', { assetIds, streamId });
    }

    copy(assetIds: string[]) {
        this.storageService.set('clipboard', { assetIds, streamId: null });
    }

    download(assetIds: string[]) {
        this.downloadService.downloadAssets(assetIds);
    }

    delete(assetIds: string[]) {
        const dialog = this.matDialog.open(DeleteAssetDialogComponent, {
            width: '80vw',
            data: assetIds,
        });
        dialog.afterClosed().subscribe((result) => {
            if (result === null) {
                return;
            }

            if (result === true) {
                // Remove from all
                this.assetService.deleteAssets(assetIds);
            } else {
                // Remove form this stream
                this.stateService.state
                    .pipe(
                        filter((state) => Boolean(state.streamId)),
                        take(1),
                    )
                    .subscribe((state) => {
                        this.streamService.removeAssets(state.streamId, assetIds);
                    });
            }
        });
    }

    getLink(assetIds: string[], streamId: string) {
        this.assetLinkService.create(assetIds, streamId).subscribe((assetLink) => {
            this.clipboard.copy(assetLink.url);
            this.matSnackBar.open('Link copied', null, { duration: 2000 });
        });
    }

    moveToStream(assetIds: string[], streamId: string, fromStreamId: string) {
        console.log('moving', assetIds, 'to', streamId);
        this.streamService.assignAssets(streamId, assetIds, fromStreamId).subscribe(() => {
            this.assetService.removeAssets(assetIds);
            this.selectionService.clearSelection();
        });
    }

    copyToStream(assetIds: string[], streamId: string) {
        console.log('copying', assetIds, 'to', streamId);
        this.streamService.assignAssets(streamId, assetIds).subscribe(() => {
            this.selectionService.clearSelection();
        });
    }
}
